import { Divider, Grid } from '@mui/material';
import { ActionButton } from '@tassat/button';
import { SelectOption } from '@tassat/common-definitions';
import { Field } from 'formik';
import * as React from 'react';
import { components, createFilter } from 'react-select';

import selectStyles from './styles';
import { SelectAssetsFieldProps, SelectAssetsStyledSelectExtraProps } from './types';
import ErrorMessage from '../ErrorMessage';
import muiStyles from '../fieldStyles';
import StyledSelect from '../StyledSelect';

const MenuListRenderer = (props: any) => {
    return (
        <Grid
            className={props.selectProps.classes.selectAssetsMenuList}
            container
            direction='row'
            spacing={0}
            wrap='wrap'
        >
            {props.children}
        </Grid>
    );
};

const MenuRenderer = (props: any) => {
    const {
        innerProps,
        selectProps,
        options
    } = props;

    const { classes, initialValues, selectedValues } = selectProps;

    return (
        <Grid
            className={classes.selectAssetsMenu}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
            {...innerProps}
        >
            <Grid
                alignItems='baseline'
                className={classes.selectAssetsMenuButtonContainer}
                container
                direction='row'
                justifyContent='flex-end'
                spacing={0}
                {...innerProps}
            >
                <div className={classes.selectAssetsMenuSelectAllButton}>
                    <input
                        checked={options.length === selectedValues.length}
                        className={classes.selectAssetsMenuSelectAllCheckbox}
                        onChange={(e) => {
                            const checked = (e.target as HTMLInputElement).checked;
                            if (checked) {
                                selectProps.setSelectedValues(options.map(({ value }) => value));
                            } else {
                                selectProps.setSelectedValues([]);
                            }
                        }}
                        type='checkbox'
                    />
                    Select All
                </div>
                <ActionButton
                    className={classes.selectAssetsMenuClearButton}
                    handleClick={() => selectProps.setSelectedValues([])}
                    text='Clear All'
                    type='muted'
                />
                <ActionButton
                    className={classes.selectAssetsMenuApplyButton}
                    disabled={initialValues.every((value) => selectedValues.includes(value)) &&
                        selectedValues.every((value) => initialValues.includes(value))}
                    handleClick={() => {
                        selectProps.onApply(selectedValues);
                        selectProps.onMenuClose();
                    }}
                    text='Apply'
                    type='primary'
                />
            </Grid>
            <Divider />
            {props.children}
        </Grid>
    );
};

const OptionRenderer = (props: any) => {
    const {
        data,
        value,
        selectProps
    } = props;

    const { classes } = selectProps;

    return (
        <Grid
            alignItems='center'
            className={classes.selectAssetsOption}
            container
            direction='row'
            item
            spacing={0}
            wrap='nowrap'
            xs={4}
        >
            <input
                checked={props.selectProps.selectedValues.includes(data.value)}
                className={classes.selectAssetsOptionCheck}
                onChange={(e) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    props.selectProps.setSelectedValues(
                        checked ?
                            [ ...selectProps.selectedValues, value ]
                            : selectProps.selectedValues.filter((prevValue) => prevValue !== data.value)
                    );
                }}
                type='checkbox'
            />
            {data.label}
        </Grid>
    );
};

const MultiValueRemoveRenderer = (props: any) => {
    const updatedProps = { ...props };
    updatedProps.innerProps.onClick = () => props.selectProps.removeSingleValue(props.data.value);

    if (!props.selectProps.options.find((opt) => opt.value === props.data.value) || props.selectProps.isDisabled) {
        return null;
    }

    return (
        <components.MultiValueRemove {...updatedProps} />
    );
};

const MultiValueLabelRenderer = (props: any) => {
    return (
        <components.MultiValueLabel {...props}>
            {props.data.label.split(' - ')[0]}
        </components.MultiValueLabel>
    );
};

export default function SelectAssetsField(props: SelectAssetsFieldProps) {
    const {
        id,
        className,
        name,
        label,
        placeholder,
        options,
        disabled,
        values,
        errors,
        autoComplete,
        fieldSize
    } = props;

    const classes = muiStyles(props);
    const customStyles = selectStyles();

    const initialValues = values[name]?.map(({ value }) => value)
        .filter((value) => options.find((opt) => opt.value === value)?.value);
    const disabledOptions = values[name]?.filter(({ value }) => !options.find((opt) => opt.value === value)?.value);

    const [ selectedValues, setSelectedValues ] = React.useState<string[]>(initialValues);

    React.useEffect(() => {
        setSelectedValues(values[name]?.map(({ value }) => value)
            .filter((value) => options.find((opt) => opt.value === value)?.value));
    }, [ name, options, values, setSelectedValues ]);

    return (
        <Field
            multiple={true}
            name={name}
            type='text'
        >
            {({ field, form }) => (
                <div className={classes.field}>
                    {label && (
                        <label
                            className={fieldSize === 'large' ? classes.inputLabelLarge : classes.inputLabel}
                            htmlFor={name}
                        >
                            {label}
                        </label>
                    )}
                    <StyledSelect<SelectOption, true, SelectAssetsStyledSelectExtraProps>
                        classes={classes}
                        className={`${className || ''} ${classes.select}`}
                        components={{
                            Option: OptionRenderer,
                            Menu: MenuRenderer,
                            MenuList: MenuListRenderer,
                            MultiValueRemove: MultiValueRemoveRenderer,
                            MultiValueLabel: MultiValueLabelRenderer
                        }}
                        customStyles={customStyles}
                        disabled={disabled}
                        filterOption={autoComplete ? createFilter({ matchFrom: 'any' }) : null}
                        hideSelectedOptions={false}
                        id={id ? `${id}_selectInput` : undefined}
                        initialValues={initialValues}
                        isClearable={false}
                        isMulti={true}
                        isSearchable={true}
                        menuPosition='fixed'
                        name={name}
                        onApply={(selectedValues) => {
                            form.setFieldTouched(name, true);

                            const values = selectedValues.map((selectedValue) => {
                                return options.find((opt) => opt.value === selectedValue);
                            });

                            values.push(...disabledOptions);

                            form.setFieldValue(name, values);
                        }}
                        options={options}
                        placeholder={placeholder}
                        removeSingleValue={(valueToRemove) => {
                            form.setFieldTouched(name, true);
                            form.setFieldValue(name, values[name].filter((val) => val.value !== valueToRemove));
                        }}
                        selectedValues={selectedValues}
                        setSelectedValues={setSelectedValues}
                        value={form.values[name]}
                    />
                    <ErrorMessage
                        error={errors[field.name]}
                        field={field}
                        form={form}
                        id={id || ''}
                    />
                </div>
            )}
        </Field>
    );
}